import { normaliseQuotePunctuation } from '../utils/scope-descriptions';
import { isGeneratedQuoteObjective } from '../utils/objective-sync';
import {
    derivePlainPhysicalScopeAction,
    generatedObjectiveContainsInternalStrategyJargon,
    readOpeningVerbs,
} from './client-scope-language';

export const RJ_BIRD_OBJECTIVE_STEM = 'The objective of this quote is to provide a price';
export const RJ_BIRD_OBJECTIVE_OPENING = `${RJ_BIRD_OBJECTIVE_STEM} to`;
export const RJ_BIRD_OBJECTIVE_ENDING = 'at the above property.';

export type RjBirdObjectiveConnective = 'to' | 'for';

// House style (Peter, 2026-09-27): "to" before a verb phrase ("to repair the
// roof membrane"), "for" before a noun phrase ("for crack repairs", "for repair
// of the retaining wall"). These are the base-form verbs that open an objective
// action. The list is small on purpose: a word that often opens a noun phrase
// after "for" ("patch repairs", "render repairs", "strip drain", "seal
// replacement", "complete replacement", "waterproof membrane", "make good
// works") is left out, so a "for" in front of it is never changed.
const OBJECTIVE_BASE_VERBS = new Set([
    'apply', 'carry', 'conduct', 'construct', 'demolish', 'erect', 'excavate',
    'fabricate', 'inspect', 'install', 'investigate', 'lay', 'perform', 'prepare',
    'rake', 'rebuild', 'rectify', 'reinstate', 'relocate', 'remediate', 'remove',
    'renew', 'repaint', 'repair', 'replace', 'repoint', 'reseal', 'resurface',
    'restore', 're-waterproof', 'supply', 'undertake',
]);
const DETERMINERS = new Set(['a', 'an', 'the']);

// The house opening names the document: "this quote". A REVISION of a quote is the
// same sentence about "this revised quote" (Peter, 2026-10-18: "The objective of this
// revised quote is to provide a price to replace the failed breeze block courtyard wall
// at Unit 6, either with a new Colorbond fence or by rebuilding the wall like for like,
// at the above property."). Every place that recognises, forms, de-duplicates or
// conforms the opening reads it through the patterns below, so the two nouns are one
// contract and the author's noun is kept wherever the sentence is rebuilt.
export const STANDARD_OBJECTIVE_OPENING_PATTERN = /^the\s+objective\s+of\s+this\s+(?:revised\s+)?quote\s+is\s+to\s+provide\s+(?:a\s+price|prices|pricing)\s+(?:to|for)\b/i;
const EXACT_OPENING = STANDARD_OBJECTIVE_OPENING_PATTERN;
const CONTRACT_CONNECTIVE = /^(the\s+objective\s+of\s+this\s+(?:revised\s+)?quote\s+is\s+to\s+provide\s+(?:a\s+price|prices|pricing)\s+)(to|for)(\s+)([\s\S]*)$/i;
const OPENING_NOUN = /^the\s+objective\s+of\s+this\s+(revised\s+)?quote\b/i;

type ObjectiveNoun = 'this quote' | 'this revised quote';

/** The noun the objective's opening uses: "this revised quote" when the author wrote it, otherwise the house "this quote". */
function openingNoun(objective: string): ObjectiveNoun {
    return OPENING_NOUN.exec(objective)?.[1] ? 'this revised quote' : 'this quote';
}

/** "The objective of this quote is to provide a price" (or "... this revised quote ..."). */
function houseStem(noun: ObjectiveNoun): string {
    return `The objective of ${noun} is to provide a price`;
}
const NESTED_PRICING_REQUEST = /^provide\s+(a\s+price|prices|pricing)\s+(to|for)\s+/i;
const EXACT_ENDING = /\bat\s+the\s+above\s+property\s*[.]?\s*$/i;
const INVESTIGATION_DELIVERABLE = /\b(?:investigat(?:e|ion|ive)?|diagnos(?:e|is|tic)?|report(?:ing)?)\b/i;
const REPAIR_DELIVERABLE = /\b(?:quote\s+to\s+)?(?:repair|replace|renew|rectif\w*|remediat\w*|rebuild|reinstate|install|make\s+good|seal|repoint)\b/i;

function clean(value: unknown): string {
    return normaliseQuotePunctuation(String(value || ''))
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function words(value: unknown): string[] {
    return clean(value)
        .toLowerCase()
        .split(' ')
        .map(word => word.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ''))
        .filter(Boolean);
}

// True only when the words open with a listed verb (or verbs joined by
// "and"/"or", each optionally followed by "out") and something that is not a
// noun reading follows. "repair the roof" and "supply and install a strip
// drain" are verb phrases. "repair of the wall", "repair works" and "supply
// and installation of" are not, and neither is a bare "repair". The derived
// objective reads a scope title with the same test (readOpeningVerbs).
function opensWithVerbPhrase(tokens: string[]): boolean {
    return readOpeningVerbs(tokens, OBJECTIVE_BASE_VERBS) === 'verb';
}

/**
 * Choose "to" or "for" for the words that follow "The objective of this quote
 * is to provide a price". The written connective is kept unless the words are
 * a confident mismatch: "for" before a bare verb phrase becomes "to", and "to"
 * before a determiner (a/an/the) becomes "for". Anything uncertain stays as
 * written. With no written connective, "to" is assumed.
 */
export function chooseObjectiveConnective(
    continuation: unknown,
    written: RjBirdObjectiveConnective = 'to',
): RjBirdObjectiveConnective {
    const tokens = words(continuation);
    if (written === 'for' && opensWithVerbPhrase(tokens)) return 'to';
    if (written === 'to' && DETERMINERS.has(tokens[0] ?? '')) return 'for';
    return written;
}

function deliveryKind(item: any): string {
    return String(
        item?.delivery_kind
        || item?.deliveryKind
        || item?.diagnosis_delivery_kind
        || item?.diagnosisDeliveryKind
        || ''
    ).trim().toLowerCase();
}

export function scopeSellsInvestigationOnly(scope: any[] = []): boolean {
    return Array.isArray(scope) && scope.some(item => deliveryKind(item) === 'investigation_only');
}

export function objectivePromisesInvestigationOrReport(value: unknown): boolean {
    return INVESTIGATION_DELIVERABLE.test(clean(value));
}

export function objectivePromisesRepair(value: unknown): boolean {
    return REPAIR_DELIVERABLE.test(clean(value));
}

export function objectiveUsesRjBirdContract(value: unknown): boolean {
    const objective = clean(value);
    return EXACT_OPENING.test(objective) && EXACT_ENDING.test(objective);
}

/**
 * The house sentence derived from the priced scope. `revised` keeps a revision's
 * noun ("this revised quote") when the objective being replaced carried it.
 */
export function deriveRjBirdObjectiveFromScope(scope: any[] = [], { revised = false }: { revised?: boolean } = {}): string {
    const action = derivePlainPhysicalScopeAction(scope);
    const opening = revised ? `${houseStem('this revised quote')} to` : RJ_BIRD_OBJECTIVE_OPENING;
    return `${opening} ${action} ${RJ_BIRD_OBJECTIVE_ENDING}`;
}

/**
 * Strip the RFQ instructions an objective action can start with: "provide a
 * price to ...", "provide pricing for ..." and "quote to ...", in whichever
 * order they come. `written` is the connective of the innermost pricing
 * request (the author's choice for the words that follow), "to" when there
 * was none, and `explicitPricingRequest` says whether there was one.
 */
function stripLeadingRfqInstructions(input: string): {
    action: string;
    written: RjBirdObjectiveConnective;
    explicitPricingRequest: boolean;
    /** The price word the author chose ("a price", "prices" or "pricing"), or null when they wrote no pricing request. */
    priceWord: string | null;
} {
    let action = input;
    let written: RjBirdObjectiveConnective = 'to';
    let explicitPricingRequest = false;
    let priceWord: string | null = null;
    for (;;) {
        const nested = action.match(NESTED_PRICING_REQUEST);
        if (nested) {
            priceWord = nested[1]!.toLowerCase().replace(/\s+/g, ' ');
            written = nested[2]!.toLowerCase() as RjBirdObjectiveConnective;
            explicitPricingRequest = true;
            action = action.slice(nested[0].length);
            continue;
        }
        const quoteTo = action.match(/^quote\s+to\s+/i);
        if (!quoteTo) break;
        action = action.slice(quoteTo[0].length);
    }
    return { action, written, explicitPricingRequest, priceWord };
}

/**
 * A proposed Objective as a sentence: first letter capital, one final full stop (a trailing comma, semicolon or colon is dropped, a
 * trailing "!" or "?" becomes the full stop). Only ever applied to a PROPOSAL; verbatim wording and stored values never come through here.
 */
function asSentence(value: string): string {
    let out = value.trim().replace(/[,;:\s]+$/, '');
    out = out.charAt(0).toUpperCase() + out.slice(1);
    if (/[!?]$/.test(out)) return `${out.slice(0, -1)}.`;
    return /\.$/.test(out) ? out : `${out}.`;
}

/**
 * What the Assistant's set_scope_objective does with the `value` it was given.
 * The tool promises the operator a COMPLETE client-facing Objective in the
 * house opening, and what is proposed is stored exactly as shown, as the
 * operator's own words (quoteObjectiveEdit, source 'assistant'). So the
 * wording is settled HERE, before the card exists, and never after Apply:
 *
 * - `as-written`          a sentence that already opens "The objective of this
 *                         quote is to provide a price|prices|pricing to|for",
 *                         already capitalised with a final full stop. Plural
 *                         and the author's words are kept.
 * - `tidied`              the same, where only the first capital or the final
 *                         full stop was missing. Nothing else changes.
 * - `connective-adjusted` the same, where only the to/for after "provide a
 *                         price" was a confident mismatch (chooseObjectiveConnective).
 * - `formed`              a bare verb phrase ("repair the balcony membrane"), the
 *                         opening the old tool text promised ("The objective of
 *                         this quote is to repair ..."), or an explicit
 *                         "provide pricing for ..." request, put into the house
 *                         sentence. The work words are the author's own, and so
 *                         is a price word they wrote ("provide prices for ..."
 *                         stays "prices"); "a price" is used only when they
 *                         wrote none.
 * - refused               anything else. Never replaced by a sentence derived
 *                         from the scope: a different action is not a repair of
 *                         this one. The Assistant is told how to resend.
 *
 * Explicit verbatim operator wording does not come through here at all (the
 * handler skips this when the operator asked for their exact words).
 */
export type AssistantObjectiveProposal =
    | { ok: true; value: string; change: 'as-written' | 'tidied' | 'connective-adjusted' | 'formed' }
    | { ok: false; reason: 'duplicate-opening' | 'not-a-complete-sentence' };

// Words that do not name any work. A tail made only of these ("to", "to the", "to at the above property") is an opening with nothing
// after it, and is refused rather than completed with a guess.
const NON_WORK_WORDS = new Set(['a', 'an', 'the', 'to', 'for', 'of', 'at', 'and', 'or', 'above', 'property']);
function namesWork(value: string): boolean {
    return words(value).some(word => !NON_WORK_WORDS.has(word));
}
// The Objective opening again, or an RFQ "quote ..." instruction, left in what follows the opening. Neither is work to price.
const RESIDUAL_OBJECTIVE_OPENING = /\bthe\s+objective\s+of\s+this\s+(?:revised\s+)?quote\s+is\s+to\b/i;
function repeatsOpeningOrRfqInstruction(tail: string): boolean {
    return NESTED_PRICING_REQUEST.test(tail) || /^quote\b/i.test(tail) || RESIDUAL_OBJECTIVE_OPENING.test(tail);
}

export function proposeAssistantObjective(typed: unknown): AssistantObjectiveProposal {
    const text = String(typed ?? '').trim();
    const objective = clean(text);
    if (!objective) return { ok: false, reason: 'not-a-complete-sentence' };

    if (EXACT_OPENING.test(objective)) {
        const parts = objective.match(CONTRACT_CONNECTIVE);
        if (!parts) return { ok: false, reason: 'not-a-complete-sentence' };
        const [, lead, writtenConnective, gap, rest] = parts;
        const afterOpening = rest!.replace(EXACT_ENDING, '').replace(/[.!?\s]+$/, '').trim();
        if (!namesWork(afterOpening)) return { ok: false, reason: 'not-a-complete-sentence' };
        if (repeatsOpeningOrRfqInstruction(afterOpening)) return { ok: false, reason: 'duplicate-opening' };
        const written = writtenConnective!.toLowerCase() as RjBirdObjectiveConnective;
        const chosen = chooseObjectiveConnective(afterOpening, written);
        const adjusted = chosen !== written;
        const value = asSentence(adjusted ? `${lead}${chosen}${gap}${rest}` : text);
        return { ok: true, value, change: adjusted ? 'connective-adjusted' : (value === text ? 'as-written' : 'tidied') };
    }

    const partialOpening = objective.match(/^the\s+objective\s+of\s+this\s+(?:revised\s+)?quote\s+is\s+to\s+([\s\S]+)$/i);
    // A partial opening keeps the noun the author wrote; a bare phrase has none and is the house noun.
    const noun = partialOpening ? openingNoun(objective) : 'this quote';
    const fragment = (partialOpening ? partialOpening[1]! : objective)
        .replace(EXACT_ENDING, '')
        .replace(/[.!?\s]+$/, '')
        .trim();
    const { action: stripped, written, explicitPricingRequest, priceWord } = stripLeadingRfqInstructions(fragment);
    const action = stripped.replace(/[.!?\s]+$/, '').trim();
    if (!namesWork(action)) return { ok: false, reason: 'not-a-complete-sentence' };
    if (repeatsOpeningOrRfqInstruction(action)) return { ok: false, reason: 'duplicate-opening' };

    const isVerbPhrase = opensWithVerbPhrase(words(action));
    // A bare phrase is formed only when its reading is certain: it opens with a
    // listed base verb ("repair the ...", "supply and install a ..."), or the
    // author wrote the pricing request themselves ("provide pricing for ...").
    // A noun phrase or a sentence about the client is not guessed at.
    if (!isVerbPhrase && !explicitPricingRequest) return { ok: false, reason: 'not-a-complete-sentence' };
    const connective = chooseObjectiveConnective(action, written);
    const work = isVerbPhrase ? action.charAt(0).toLowerCase() + action.slice(1) : action;
    // The price word is the author's when they wrote one ("provide prices for ..."); the house "a price" only when they did not.
    const opening = priceWord ? `The objective of ${noun} is to provide ${priceWord}` : houseStem(noun);
    return { ok: true, value: asSentence(`${opening} ${connective} ${work} ${RJ_BIRD_OBJECTIVE_ENDING}`), change: 'formed' };
}

/**
 * Conform AI/summary/RFQ-derived objectives without touching a builder edit.
 * Unsupported investigation/report wording is replaced from the priced scope;
 * explicitly sold investigation-only rows remain eligible for that wording.
 * The connective after "provide a price" is "to" before a verb phrase and
 * "for" before a noun phrase (see chooseObjectiveConnective).
 */
export function conformGeneratedRjBirdObjective(candidate: unknown, scope: any[] = []): string {
    const objective = clean(candidate);
    const internalStrategyLeak = generatedObjectiveContainsInternalStrategyJargon(objective);
    const investigationSold = scopeSellsInvestigationOnly(scope);
    const diagnosisSupportsRepair = objectivePromisesInvestigationOrReport(objective)
        && objectivePromisesRepair(objective);
    const repeatsPricingRequest = /^the\s+objective\s+of\s+this\s+(?:revised\s+)?quote\s+is\s+to\s+provide\s+(?:a\s+price|prices|pricing)\s+(?:to|for)\s+provide\s+(?:a\s+price|prices|pricing)\s+(?:to|for)\b/i
        .test(objective);
    const startsWithQuoteInstruction = /^the\s+objective\s+of\s+this\s+(?:revised\s+)?quote\s+is\s+to\s+provide\s+(?:a\s+price|prices|pricing)\s+(?:to|for)\s+quote\b/i
        .test(objective);
    // A revision's noun survives any rebuild of its own sentence.
    const noun = openingNoun(objective);
    if (objectiveUsesRjBirdContract(objective)
        && !internalStrategyLeak
        && !repeatsPricingRequest
        && !startsWithQuoteInstruction
        && (!objectivePromisesInvestigationOrReport(objective) || investigationSold || diagnosisSupportsRepair)) {
        // Keep the author's "to" or "for"; change it only on a confident
        // mismatch. The choice reads the author's words, not the fixed ending:
        // "for repaint at the above property" keeps its "for", as "for
        // repaint" does when the rebuild below adds the ending.
        const parts = objective.match(CONTRACT_CONNECTIVE);
        if (!parts) return objective;
        const [, lead, writtenConnective, gap, rest] = parts;
        const written = writtenConnective!.toLowerCase() as RjBirdObjectiveConnective;
        const chosen = chooseObjectiveConnective(rest!.replace(EXACT_ENDING, ''), written);
        return chosen === written ? objective : `${lead}${chosen}${gap}${rest}`;
    }

    if (objective
        && !internalStrategyLeak
        && (!objectivePromisesInvestigationOrReport(objective) || investigationSold || diagnosisSupportsRepair)) {
        const match = objective.match(/^the\s+objective\s+of\s+this\s+(?:revised\s+)?quote\s+is\s+to\s+(.+?)(?:\s+at\s+the\s+above\s+property)?[.!?]?$/i);
        if (match) {
            let action = clean(match[1]!).replace(/[.!?]+$/, '');
            // RFQs commonly say "provide pricing to ..." or "provide pricing
            // for ...". When that request is wrapped in the RJ Bird opening,
            // strip every nested pricing instruction so the objective cannot
            // read "provide a price to provide pricing to ...". The connective
            // of the innermost instruction is the author's choice for the words
            // that follow; with none, the "is to" of the opening stands.
            // A leading "quote to" is an RFQ instruction to the builder and is
            // stripped in the same pass, in whichever order the two come:
            // stripped after the pricing requests, "quote to provide a price
            // for X" read "provide a price to provide a price for X", which
            // a second conform then changed (Stage 5 conforms the draft's
            // objective again).
            const stripped = stripLeadingRfqInstructions(action);
            action = stripped.action;
            const written = stripped.written;
            // "Quote eave repairs" is an RFQ instruction to the builder, not
            // the work action a client objective should promise. Keep the
            // bounded diagnose-and-repair wording (where "quote to repair" is
            // part of a larger RFQ-supported clause), but naturalise a leading
            // quote instruction into the action being priced.
            action = action.replace(/^quote\s+/i, 'undertake ');
            if (action) {
                const connective = chooseObjectiveConnective(action, written);
                const rebuilt = `${houseStem(noun)} ${connective} ${action} ${RJ_BIRD_OBJECTIVE_ENDING}`;
                // What is left can be a generic action ("repair", "undertake
                // repair") that the sentence as a whole did not show. The
                // rebuilt sentence is held to the check above, or conforming
                // it again would replace it.
                if (!generatedObjectiveContainsInternalStrategyJargon(rebuilt)) return rebuilt;
            }
        }
    }

    return deriveRjBirdObjectiveFromScope(scope, { revised: noun === 'this revised quote' });
}

/**
 * Stage 4 and Stage 5 share the same generated-copy boundary. Builder-authored
 * wording is authoritative; every other objective is conformed before display.
 */
export function resolveRjBirdObjectiveForStage(candidate: unknown, scope: any[] = [], { editedByUser = false }: { editedByUser?: boolean } = {}): string {
    if (editedByUser) return String(candidate || '');
    return conformGeneratedRjBirdObjective(candidate, scope);
}

/**
 * While the operator's own words stand in the quote (storeQuoteObjective),
 * the form keeps the quote's own objective, the one it was generated or
 * imported with, and which quote that was. Two plain text fields, not an
 * object, so every copy of the form keeps them.
 */
export const QUOTE_OWN_OBJECTIVE_FIELD = 'scopeObjectiveQuoteOwn';
export const QUOTE_OWN_OBJECTIVE_OF_FIELD = 'scopeObjectiveQuoteOwnOf';

/** The stored values a quote's objective is read from. Nothing else is. */
export interface QuoteObjectiveInputs {
    formData?: {
        scopeObjective?: unknown;
        scopeObjectiveEditedByUser?: unknown;
        scopeObjectiveQuoteOwn?: unknown;
        scopeObjectiveQuoteOwnOf?: unknown;
    } | null;
    quoteData?: {
        scopeObjective?: unknown;
        meta?: { generatedBy?: unknown; generatedAt?: unknown; scopeObjectiveFromOperator?: unknown } | null;
    } | null;
    scope?: any[] | null;
    /** The Stage 1.5 project summary; only its objective is read. */
    projectSummary?: { scope_objective?: unknown; scopeObjective?: unknown } | null;
}

/**
 * Which quote an objective belongs to. A regenerated quote is another quote
 * (a new generatedAt); Save, Back and the copy Stage 5 holds are the same one.
 * An import builds a form of its own (map-to-quote.ts), and a split drops the
 * kept objective from the form of a half it gives a new one (quote-split.js
 * splitFormData).
 */
export function quoteObjectiveIdentity(quoteData: QuoteObjectiveInputs['quoteData']): string {
    const meta = quoteData?.meta || {};
    return `${String(meta.generatedBy ?? '')}|${String(meta.generatedAt ?? '')}`;
}

/**
 * The quote's own objective: the one it was generated or imported with. The
 * quote itself carries the operator's words while they stand
 * (storeQuoteObjective), so its own objective is then read from the form, for
 * that quote only. Any copy of the quote (the one Stage 5 holds, a saved one)
 * gives the same answer.
 */
export function quoteOwnObjective({ formData = null, quoteData = null }: QuoteObjectiveInputs = {}): unknown {
    const kept = formData?.scopeObjectiveQuoteOwn;
    if (typeof kept === 'string'
        && quoteData
        && typeof quoteData === 'object'
        && formData?.scopeObjectiveQuoteOwnOf === quoteObjectiveIdentity(quoteData)) {
        return kept;
    }
    return quoteData?.scopeObjective;
}

/** The operator's own words for the objective, or null when there are none. */
export function operatorObjectiveWords(formData: QuoteObjectiveInputs['formData']): string | null {
    const text = formData?.scopeObjective;
    return formData?.scopeObjectiveEditedByUser === true && clean(text) ? String(text) : null;
}

/**
 * THE objective of a quote (Peter, 2026-09-29: "Stage 4 and Stage 5 Scope
 * Objectives should ALWAYS match"). Stage 4, Stage 5, the printed page, the
 * PDF and email snapshot, issue readiness and Save all show this one value;
 * App computes it once per render (useQuoteObjective) and hands it to both
 * stages. No stage computes its own.
 *
 * Read in this order, from the stored values only:
 * 1. The operator's own words (`formData.scopeObjective` with
 *    `scopeObjectiveEditedByUser`), exactly as written. An empty or blank
 *    stored objective is not words: it falls through to the steps below.
 * 2. A generated quote's own objective (quoteOwnObjective, of a quote
 *    written by the AI or the Stage 5 draft), conformed. Generation writes
 *    `quoteData`, so the render that stores a new quote shows its objective
 *    in both stages at once.
 * 3. An objective stored on the form that the operator did not write,
 *    conformed.
 * 4. A legacy or imported quote's own objective, exactly as written:
 *    QuoteMaker cannot prove who wrote it.
 * 5. The Stage 1.5 summary's objective (`projectSummary.scope_objective`),
 *    conformed: the objective written for the project before any quote.
 * 6. A sentence derived from the scope.
 *
 * With no quote yet, Stage 5 opens a draft built from the scope
 * (buildStage5DraftQuoteFromScope) without storing it. The draft takes its
 * objective from this function with no quote, over the same values, so it
 * says what Stage 4 shows before Stage 5 opens and after Back.
 */
export function resolveQuoteObjective({ formData = null, quoteData = null, scope = [], projectSummary = null }: QuoteObjectiveInputs = {}): string {
    const items = Array.isArray(scope) ? scope : [];
    const formObjective = formData?.scopeObjective;
    const quoteObjective = quoteOwnObjective({ formData, quoteData });
    const formHasText = clean(formObjective).length > 0;
    const quoteHasText = clean(quoteObjective).length > 0;

    if (formHasText && formData?.scopeObjectiveEditedByUser === true) {
        return resolveRjBirdObjectiveForStage(formObjective, items, { editedByUser: true });
    }
    if (quoteHasText && isGeneratedQuoteObjective(quoteData?.meta?.generatedBy)) {
        return resolveRjBirdObjectiveForStage(quoteObjective, items);
    }
    if (formHasText) {
        return resolveRjBirdObjectiveForStage(formObjective, items);
    }
    if (quoteHasText) {
        return resolveRjBirdObjectiveForStage(quoteObjective, items, { editedByUser: true });
    }
    const summaryObjective = projectSummary?.scope_objective || projectSummary?.scopeObjective;
    if (clean(summaryObjective)) {
        return resolveRjBirdObjectiveForStage(summaryObjective, items);
    }
    return deriveRjBirdObjectiveFromScope(items);
}

/**
 * Who wrote an objective change. `save` is Stage 5 Save storing the objective
 * it showed.
 */
export type QuoteObjectiveEditSource = 'user' | 'assistant' | 'history' | 'save';

/**
 * How every path stores an objective: typing in Stage 4 or Stage 5, an
 * applied Assistant change, Undo/Redo or a regeneration merge in Stage 5, and
 * Stage 5 Save. Returns the two form fields to store. A stored objective with
 * text always resolves to exactly `value`, so both stages show what was
 * written, and Save never changes the objective both stages show.
 *
 * An empty or blank objective is nobody's words. It is stored as no objective
 * (`''`, not the operator's), which gives the objective back to the quote: its
 * generated or imported objective, or a sentence from the scope. Both stages
 * then show that one (Peter, 2026-09-29: they must always match).
 *
 * Typing and Assistant changes are the operator's own words, so no later
 * generation or derivation replaces them. An Undo/Redo or Save value is
 * claimed as the operator's only when the resolver would not already produce
 * it, so undoing back to the generated sentence leaves it generated, and Save
 * of an imported quote keeps its objective as written. Save never takes the
 * operator's own words away from them.
 *
 * A quote generated while the operator's own words stood carries them, tidied
 * (quote-generation.js resolveScopeObjective), and says so
 * (`meta.scopeObjectiveFromOperator`). Its objective is theirs: a Stage 5
 * regeneration, Undo or Redo that stores it keeps it theirs, so the next
 * generation carries it again rather than the AI's objective.
 */
export function quoteObjectiveEdit(
    value: unknown,
    {
        source = 'user',
        formData = null,
        quoteData = null,
        scope = [],
    }: { source?: QuoteObjectiveEditSource | string | null } & QuoteObjectiveInputs = {},
): { scopeObjective: string; scopeObjectiveEditedByUser: boolean } {
    const scopeObjective = String(value ?? '');
    if (!clean(scopeObjective)) return { scopeObjective: '', scopeObjectiveEditedByUser: false };
    if (source !== 'history' && source !== 'save') return { scopeObjective, scopeObjectiveEditedByUser: true };
    if (source === 'save' && formData?.scopeObjectiveEditedByUser === true && clean(formData?.scopeObjective)) {
        return { scopeObjective, scopeObjectiveEditedByUser: true };
    }
    const unowned = resolveQuoteObjective({
        formData: { ...(formData || {}), scopeObjective, scopeObjectiveEditedByUser: false },
        quoteData,
        scope,
    });
    if (unowned !== scopeObjective) return { scopeObjective, scopeObjectiveEditedByUser: true };
    const quoteCarriesOperatorWords = source === 'history'
        && quoteData?.meta?.scopeObjectiveFromOperator === true;
    return { scopeObjective, scopeObjectiveEditedByUser: quoteCarriesOperatorWords };
}

/**
 * The quote as it is stored, saying what both stages show. The saved quote is
 * what everything after the stages reads: the copy kept when it is sent, the
 * "changed since it was sent" check, the revision diff, the Run Sheet, the
 * Assistant outside Stage 5, search and the learning corpus all read
 * `quoteData.scopeObjective`. An objective edit stored only on the form left
 * every one of them on the old objective.
 *
 * - While the operator's own words are stored on the form, the quote carries
 *   them too. The quote's own objective is kept on the form for that quote
 *   (quoteOwnObjective), so clearing the words or an Undo gives it back.
 * - When the words go and the quote still carries them (`previousWords`, the
 *   operator's words before this change), the quote gets its own objective
 *   back.
 *
 * Anything else is left as stored. Returns the same objects when nothing
 * changes. Without a quote there is nothing to write: generation stores the
 * operator's words on the quote it makes.
 */
export function storeQuoteObjective({
    formData = null,
    quoteData = null,
    previousWords = null,
}: QuoteObjectiveInputs & { previousWords?: string | null } = {}): { formData: any; quoteData: any } {
    if (!quoteData || typeof quoteData !== 'object' || Array.isArray(quoteData)) return { formData, quoteData };
    const words = operatorObjectiveWords(formData);
    const own = quoteOwnObjective({ formData, quoteData });
    const ownText = typeof own === 'string' ? own : '';
    if (words !== null) {
        const identity = quoteObjectiveIdentity(quoteData);
        const ownKept = typeof formData?.scopeObjectiveQuoteOwn === 'string'
            && formData?.scopeObjectiveQuoteOwnOf === identity;
        return {
            formData: ownKept || ownText === words
                ? formData
                : { ...formData, [QUOTE_OWN_OBJECTIVE_FIELD]: ownText, [QUOTE_OWN_OBJECTIVE_OF_FIELD]: identity },
            quoteData: quoteData.scopeObjective === words ? quoteData : { ...quoteData, scopeObjective: words },
        };
    }
    if (previousWords !== null && quoteData.scopeObjective === previousWords && ownText !== previousWords) {
        return { formData, quoteData: { ...quoteData, scopeObjective: ownText } };
    }
    return { formData, quoteData };
}
